import { getApiMessage } from "../config/taskApi";
import {
  getApplyLoanDigitapDraft,
  saveApplyLoanDigitapCallbackTxn,
  saveApplyLoanDigitapDraft,
} from "./applyLoanStorage";

const isObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

/** Normalize API payload — parse digitap_response when JSON string */
export const unwrapDigitapPayload = (raw) => {
  const payload = isObject(raw) ? { ...raw } : {};
  const digitapRaw = payload.digitap_response ?? payload.digitapResponse;
  if (digitapRaw != null) {
    payload.digitap_response = tryParseJson(digitapRaw);
  }
  if (isObject(payload.data)) {
    const nestedDigitap =
      payload.data.digitap_response ?? payload.data.digitapResponse;
    if (nestedDigitap != null) {
      payload.data = {
        ...payload.data,
        digitap_response: tryParseJson(nestedDigitap),
      };
    }
  }
  if (isObject(payload.Data)) {
    const nestedDigitap =
      payload.Data.digitap_response ?? payload.Data.digitapResponse;
    if (nestedDigitap != null) {
      payload.Data = {
        ...payload.Data,
        digitap_response: tryParseJson(nestedDigitap),
      };
    }
  }
  return payload;
};

const deepFindValue = (node, keys, depth = 0) => {
  if (!node || depth > 8) return "";
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = deepFindValue(item, keys, depth + 1);
      if (found) return found;
    }
    return "";
  }
  if (!isObject(node)) return "";

  for (const key of keys) {
    const value = node[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }

  for (const value of Object.values(node)) {
    const found = deepFindValue(value, keys, depth + 1);
    if (found) return found;
  }
  return "";
};

export const getDigitapModel = (raw) => {
  const payload = unwrapDigitapPayload(raw);
  const candidates = [
    payload?.digitap_response?.model,
    payload?.digitapResponse?.model,
    payload?.data?.digitap_response?.model,
    payload?.data?.digitapResponse?.model,
    payload?.Data?.digitap_response?.model,
    payload?.data?.model,
    payload?.Data?.model,
    payload?.model,
  ];
  for (const candidate of candidates) {
    if (isObject(candidate)) return candidate;
  }
  return null;
};

export const getDigitapKycUrl = (raw) => {
  const payload = unwrapDigitapPayload(raw);
  const model = getDigitapModel(payload);
  // Prefer DigiLocker kycUrl over short smler url
  const fromModel = model?.kycUrl || model?.kyc_url || "";
  if (fromModel) return String(fromModel);
  if (model?.url) return String(model.url);

  const dig =
    payload.digitap_response ||
    payload.digitapResponse ||
    payload.data?.digitap_response ||
    payload.data?.digitapResponse;
  if (isObject(dig)) {
    const fromDig =
      dig.kycUrl || dig.kyc_url || dig.model?.kycUrl || dig.model?.kyc_url;
    if (fromDig) return String(fromDig);
    if (dig.url || dig.model?.url) return String(dig.url || dig.model.url);
  }

  return (
    deepFindValue(payload, ["kycUrl", "kyc_url"]) ||
    deepFindValue(payload, ["url"]) ||
    ""
  );
};

/** True when start API has usable DigiLocker session (ignore top-level status:false) */
export const hasDigitapStartSession = (raw) =>
  Boolean(getDigitapKycUrl(raw) && getDigitapTransactionId(raw));

export const getDigitapTransactionId = (raw) => {
  const payload = unwrapDigitapPayload(raw);
  const model = getDigitapModel(payload);
  const rawId =
    model?.transactionId ||
    model?.txnId ||
    model?.transaction_id ||
    payload.transactionId ||
    payload.transaction_id ||
    payload.txnId ||
    payload.data?.transactionId ||
    payload.data?.transaction_id ||
    deepFindValue(payload, [
      "transactionId",
      "transaction_id",
      "txnId",
      "txn_id",
    ]) ||
    "";

  return String(rawId).replace(/[{}]/g, "").replace(/\D/g, "");
};

const flattenDigitapAddress = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address.trim();
  if (!isObject(address)) return "";
  return [
    address.care_of || address.careOf,
    address.house,
    address.street,
    address.landmark,
    address.loc || address.locality,
    address.post_office || address.postOffice,
    address.vtc || address.vtcName || address.village,
    address.dist || address.district,
    address.state,
    address.pc || address.pincode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const splitFullName = (fullName = "") => {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return { firstName: "", middleName: "", lastName: "" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], middleName: "", lastName: "" };
  }
  if (parts.length === 2) {
    return { firstName: parts[0], middleName: "", lastName: parts[1] };
  }
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
};

/** Walk Status/Data wrappers until KYC person row (name + address fields). */
const unwrapKycPersonNode = (node, depth = 0) => {
  if (!isObject(node) || depth > 6) return null;

  const nested = isObject(node.Data)
    ? node.Data
    : isObject(node.data)
      ? node.data
      : null;

  const hasPerson =
    node.name ||
    node.customer_name ||
    node.fullName ||
    node.full_name ||
    node.dob ||
    node.dateOfBirth ||
    node.gender;
  const hasAddress =
    node.full_address ||
    node.fullAddress ||
    node.house ||
    node.street ||
    node.locality ||
    (typeof node.address === "string" && node.address.trim()) ||
    isObject(node.address);

  // Prefer nested person row when this layer is only a Status/Message wrapper
  if (nested) {
    const fromNested = unwrapKycPersonNode(nested, depth + 1);
    if (fromNested) {
      const nestedHasAddress =
        fromNested.full_address ||
        fromNested.fullAddress ||
        fromNested.house ||
        fromNested.street;
      if (nestedHasAddress || !hasAddress) return fromNested;
    }
  }

  if (hasPerson || hasAddress) return node;
  return null;
};

const pickProfileRoot = (raw) => {
  const payload = unwrapDigitapPayload(raw);

  // Prefer flattened KYC Data row (has full_address) over digitap model
  const fromData = unwrapKycPersonNode(payload);
  if (fromData && (fromData.full_address || fromData.fullAddress || fromData.house)) {
    return fromData;
  }

  const model = getDigitapModel(payload);
  if (model) {
    const deepFull = deepFindValue(payload, ["full_address", "fullAddress", "aadhaar_address"]);
    if (deepFull && !model.full_address && !model.fullAddress) {
      return { ...model, full_address: deepFull, fullAddress: deepFull };
    }
    return model;
  }

  if (fromData) return fromData;

  const candidates = [
    payload.Data,
    payload.data,
    payload.profile,
    payload.kycDetails,
    payload.result,
    payload.digitap_response,
    payload.digitapResponse,
    payload,
  ];
  for (const c of candidates) {
    const unwrapped = unwrapKycPersonNode(c);
    if (unwrapped) return unwrapped;
    if (
      isObject(c) &&
      (c.name ||
        c.customer_name ||
        c.fullName ||
        c.full_name ||
        c.dob ||
        c.dateOfBirth ||
        c.gender)
    ) {
      return c;
    }
  }

  // Deep fallback: rebuild a minimal person object INCLUDING address
  const deepName = deepFindValue(payload, [
    "name",
    "customer_name",
    "fullName",
    "full_name",
  ]);
  if (deepName) {
    return {
      name: deepName,
      customer_name: deepName,
      dob: deepFindValue(payload, ["dob", "dateOfBirth", "date_of_birth"]),
      gender: deepFindValue(payload, ["gender"]),
      full_address: deepFindValue(payload, [
        "full_address",
        "fullAddress",
        "aadhaar_address",
      ]),
      house: deepFindValue(payload, ["house"]),
      street: deepFindValue(payload, ["street"]),
      landmark: deepFindValue(payload, ["landmark"]),
      locality: deepFindValue(payload, ["locality", "loc"]),
      post_office: deepFindValue(payload, ["post_office", "postOffice"]),
      vtc: deepFindValue(payload, ["vtc", "vtcName", "village"]),
      district: deepFindValue(payload, ["district", "dist"]),
      state: deepFindValue(payload, ["state"]),
      pincode: deepFindValue(payload, ["pincode", "pc", "pin"]),
      country: deepFindValue(payload, ["country"]),
      care_of: deepFindValue(payload, ["care_of", "careOf"]),
      maskedAdharNumber: deepFindValue(payload, [
        "maskedAdharNumber",
        "maskedAadhaarNumber",
        "masked_aadhaar",
        "masked_aadhaar_number",
      ]),
    };
  }
  return null;
};

export const extractDigitapProfile = (raw) => {
  const payload = unwrapDigitapPayload(raw);
  const root = pickProfileRoot(payload);
  if (!root) return null;

  const fullName = String(
    root.name || root.customer_name || root.fullName || root.full_name || ""
  ).trim();
  const { firstName, middleName, lastName } = splitFullName(fullName);
  const addressObj = isObject(root.address) ? root.address : null;
  const deepFullAddress = deepFindValue(payload, [
    "full_address",
    "fullAddress",
    "aadhaar_address",
  ]);
  // Prefer API full_address (DigiLocker KYC Data.full_address)
  const address =
    String(root.full_address || root.fullAddress || root.aadhaar_address || "").trim() ||
    String(deepFullAddress || "").trim() ||
    flattenDigitapAddress(root) ||
    flattenDigitapAddress(addressObj || root.address);
  const masked = String(
    root.maskedAdharNumber ||
      root.maskedAadhaarNumber ||
      root.masked_aadhaar ||
      root.masked_aadhaar_number ||
      root.aadhaar_number ||
      ""
  ).trim();
  const digits = masked.replace(/\D/g, "");
  const aadhaarLastFour = digits.slice(-4);

  return {
    name: fullName,
    fullName,
    firstName,
    middleName,
    lastName,
    dob: root.dob || root.dateOfBirth || root.date_of_birth || "",
    gender: root.gender || "",
    address,
    full_address: address,
    fullAddress: address,
    district: addressObj?.dist || addressObj?.district || root.district || root.dist || "",
    state: addressObj?.state || root.state || "",
    pincode: addressObj?.pc || addressObj?.pincode || root.pincode || root.pc || "",
    mobile: root.mobile || root.mobileNumber || root.phone || "",
    email: root.email || root.emailId || "",
    maskedAdharNumber: masked,
    aadhaarLastFour,
    digitapStatus: root.status || root.verification_status || "",
  };
};

/**
 * Parse getDigitapKycDetails poll result.
 * Success = name/customer_name/fullName present (Status Success preferred).
 * "Failed" + "KYC details not found" = NOT READY → keep polling.
 */
export const parseDigitapKycDetailsStatus = (raw) => {
  const payload = unwrapDigitapPayload(raw);
  const statusRaw = String(
    payload.Status ??
      payload.status ??
      payload.data?.Status ??
      payload.Data?.Status ??
      payload.digitap_response?.model?.status ??
      ""
  ).trim();
  const status = statusRaw.toLowerCase();
  const messageRaw = String(
    payload.Message ??
      payload.message ??
      payload.data?.Message ??
      payload.Data?.Message ??
      ""
  ).trim();
  const messageLower = messageRaw.toLowerCase();

  const profile = extractDigitapProfile(payload);
  const hasIdentity = Boolean(
    profile && (profile.name || profile.dob || profile.gender)
  );

  const model = getDigitapModel(payload);
  const modelOk =
    (String(model?.status || "").toLowerCase() === "s" ||
      String(model?.status || "").toLowerCase() === "success") &&
    Boolean(model?.name || model?.customer_name || model?.fullName || model?.dob);

  if (hasIdentity || modelOk) {
    return {
      state: "success",
      profile:
        profile ||
        extractDigitapProfile({ digitap_response: { model } }),
      message: "",
    };
  }

  if (
    status === "cancelled" ||
    status === "canceled" ||
    status === "rejected" ||
    messageLower.includes("cancelled") ||
    messageLower.includes("canceled") ||
    messageLower.includes("rejected")
  ) {
    return {
      state: "failed",
      profile: null,
      message:
        messageRaw ||
        "DigiLocker verification failed. Click Continue to try again.",
    };
  }

  if (
    status === "failed" ||
    status === "error" ||
    status === "pending" ||
    status === "" ||
    messageLower.includes("not found") ||
    messageLower.includes("pending") ||
    messageLower.includes("not ready") ||
    messageLower.includes("waiting")
  ) {
    return {
      state: "pending",
      profile: null,
      message:
        "Complete Aadhaar verification in the DigiLocker window. Keep this page open.",
    };
  }

  if (status === "success" || status === "1" || status === "completed") {
    return {
      state: "pending",
      profile: null,
      message: "Waiting for DigiLocker identity details...",
    };
  }

  return {
    state: "pending",
    profile: null,
    message:
      "Complete Aadhaar verification in the DigiLocker window. Keep this page open.",
  };
};

export const isDigitapKycComplete = (raw) =>
  parseDigitapKycDetailsStatus(raw).state === "success";

export const getDigitapUserMessage = (raw, fallback = "") => {
  const payload = unwrapDigitapPayload(raw);
  if (isDigitapKycComplete(payload)) {
    return getApiMessage(payload, "") || fallback;
  }
  return (
    getApiMessage(payload?.data, "") ||
    getApiMessage(payload?.Data, "") ||
    getApiMessage(payload, "") ||
    fallback
  );
};

export const readApplyLoanDigitapReturnParams = () => {
  if (typeof window === "undefined") {
    return { txnId: "", aadhaarVerified: false };
  }
  const params = new URLSearchParams(window.location.search);
  const txnRaw =
    params.get("txnId") ||
    params.get("txn_id") ||
    params.get("transactionId") ||
    params.get("transaction_id") ||
    "";
  const txnId = String(txnRaw).replace(/[{}]/g, "").replace(/\D/g, "");
  const verifiedFlag = String(
    params.get("aadhaarVerified") ||
      params.get("aadhaar_verified") ||
      params.get("success") ||
      ""
  ).toLowerCase();
  return {
    txnId,
    aadhaarVerified:
      verifiedFlag === "1" ||
      verifiedFlag === "true" ||
      verifiedFlag === "success",
  };
};

export const clearApplyLoanDigitapReturnParams = () => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  [
    "txnId",
    "txn_id",
    "transactionId",
    "transaction_id",
    "aadhaarVerified",
    "aadhaar_verified",
    "success",
  ].forEach((key) => url.searchParams.delete(key));
  window.history.replaceState({}, "", url.pathname);
};

/** postMessage type: DigiLocker popup → original form tab */
export const DIGITAP_CALLBACK_MESSAGE_TYPE = "FW_DIGITAP_KYC_CALLBACK";

/**
 * DigiLocker backend redirects into the popup tab.
 * If this window was opened from the form, hand txn back to opener and close popup
 * so the user stays on the original form tab (not a second /landing or /apply-loan).
 *
 * @returns {boolean} true when this tab is a DigiLocker popup handoff (do not render form)
 */
export const handoffDigitapCallbackToOpenerIfNeeded = () => {
  if (typeof window === "undefined") return false;

  const cb = readApplyLoanDigitapReturnParams();
  if (!cb.txnId && !cb.aadhaarVerified) return false;

  const opener = window.opener;
  if (!opener || opener.closed) return false;

  try {
    // Persist for opener (same-origin localStorage)
    if (cb.txnId) {
      saveApplyLoanDigitapCallbackTxn(cb.txnId);
      const existing = getApplyLoanDigitapDraft();
      saveApplyLoanDigitapDraft({
        pan: existing?.pan || "",
        transactionId: cb.txnId,
        kycUrl: existing?.kycUrl || "",
        startedAt: existing?.startedAt || Date.now(),
        mobileVerified: true,
        panVerified: true,
      });
    }

    opener.postMessage(
      {
        type: DIGITAP_CALLBACK_MESSAGE_TYPE,
        txnId: cb.txnId || "",
        aadhaarVerified: Boolean(cb.aadhaarVerified),
      },
      window.location.origin
    );
  } catch {
    /* ignore */
  }

  try {
    opener.focus();
  } catch {
    /* ignore */
  }

  clearApplyLoanDigitapReturnParams();

  try {
    window.close();
  } catch {
    /* ignore */
  }

  // Some browsers delay close — retry
  setTimeout(() => {
    try {
      window.close();
    } catch {
      /* ignore */
    }
  }, 100);

  return true;
};
